import { useCallback, useMemo } from 'react';
import { SsoService, TenantSlugDiscovery } from 'api';
import { FIRM_WATERFALL } from 'common/constants/user';
import { WATERFALL_SLUG } from 'common/constants/waterfall';
import { Logger } from 'utilities';

// waterfall pages paths looks like this
// https://host/firms/firm-slug/companies/company_slug/waterfall?cap-table-version=cap-table-19833&date=2025-09-30&exit-date=2026-03-03
// or
// https://host/firms/firm-slug/firm-waterfall?cap-table-version=cap-table-19833&date=2025-09-30&exit-date=2026-03-03
const WATERFALL_REGEX = new RegExp(String.raw`^/firms/[\w-]+/(companies/[\w-]+/${WATERFALL_SLUG}|${FIRM_WATERFALL})$`);
const entityMap = {
  firms: TenantSlugDiscovery.entity.FIRM,
  companies: TenantSlugDiscovery.entity.COMPANY,
};

const useTenantDiscovery = ({ location }: { location: string }) => {
  const needsDiscovery = useMemo(() => {
    const url = new URL(location);
    return WATERFALL_REGEX.test(url.pathname);
  }, [location]);

  const { slug, entity } = useMemo(() => {
    const url = new URL(location);
    const pathSegments = url.pathname.split('/').filter(Boolean);
    // Assuming the URL structure is /{entity}/{slug}/... (e.g., /firm/abc123/)
    const entity = pathSegments.length > 0 ? pathSegments[0] : '';
    const slug = pathSegments.length > 1 ? pathSegments[1] : '';
    return { slug, entity };
  }, [location]);

  const discoverTenantBySlug = useCallback(async () => {
    const requestEntity = entityMap[entity as keyof typeof entityMap];
    if (!slug || !requestEntity) {
      Logger.error('Invalid URL structure for tenant discovery. Slug or entity is missing or unrecognized.', {
        slug,
        entity,
      });
      return;
    }
    try {
      const response = await SsoService.apiSsoSlugDiscoveryCreate({
        slug,
        entity: requestEntity,
        redirect_to: location,
      });
      return response;
    } catch (error) {
      Logger.error('Error discovering tenant by slug for SSO.', error);
    }
  }, [location, slug, entity]);

  return { discoverTenantBySlug, needsDiscovery };
};

export default useTenantDiscovery;
